import { useEffect, useRef } from 'react';
import { clockNow, useClock } from '@/state/store';
import { WEATHER } from '../constants';
import { useLoader } from '../store';
import { fetchWeatherMetadata, resolveWeatherDate } from './imagery';
import { useWeatherOverlay } from './store';

/** Resolve imagery independently of toolbar visibility (including presenter mode). */
export function useWeatherImagery(parked: boolean) {
  const key = useLoader((s) => s.weatherLayer);
  const mode = useWeatherOverlay((s) => s.mode);
  const refreshId = useWeatherOverlay((s) => s.refreshId);
  const lastRefresh = useRef(refreshId);
  const day = useClock((s) =>
    mode === 'timeline' && !s.live ? new Date(s.simTime).toISOString().slice(0, 10) : 'present',
  );
  useEffect(() => {
    if (!key || parked) return;
    const timer = setInterval(() => useWeatherOverlay.getState().refresh(), WEATHER.REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [key, parked]);

  useEffect(() => {
    if (parked) return;
    let disposed = false;
    const set = useWeatherOverlay.setState;
    set({
      request: null,
      metadata: null,
      phase: key ? 'loading' : 'off',
      message: null,
      tileError: null,
      checkedAt: null,
    });
    const config = key ? WEATHER.LAYERS[key] : null;
    if (!key || !config) return;
    if (mode === 'timeline' && !config.gibs) {
      set({
        phase: 'unavailable',
        message:
          'This service supplies latest imagery only. Choose Latest available, or a NASA daily layer to follow the timeline.',
      });
      return;
    }
    const force = refreshId !== lastRefresh.current;
    lastRefresh.current = refreshId;
    void fetchWeatherMetadata(config, force)
      .then((metadata) => {
        if (disposed) return;
        const requestedDay = day === 'present' ? new Date(clockNow()).toISOString().slice(0, 10) : day;
        const time = config.gibs ? resolveWeatherDate(metadata, mode, requestedDay) : undefined;
        if (config.gibs && !time) {
          set({
            metadata,
            phase: 'unavailable',
            message: `No imagery is advertised for ${requestedDay}. Choose Latest available or another timeline date.`,
            checkedAt: Date.now(),
          });
          return;
        }
        set({
          request: { key, time: time ?? undefined, revision: Date.now() },
          metadata,
          phase: 'available',
          checkedAt: Date.now(),
        });
      })
      .catch((error: unknown) => {
        if (!disposed)
          set({
            phase: 'error',
            message:
              error instanceof Error ? error.message : 'Weather imagery is unavailable. Please refresh.',
          });
      });
    return () => {
      disposed = true;
    };
  }, [key, mode, day, refreshId, parked]);
}
